import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent,
  ConfirmModalComponent,
  EmptyPlaceholderComponent,
  HelpTooltipComponent,
  SegmentedControlComponent,
  SegmentedControlItem,
  TableSearchInputComponent,
  TagComponent,
  TagVariant,
} from '@catch/general-ui';
import { TooltipDirective } from '@catch/directives';
import { ToastsService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBriefcase,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TableModule } from 'primeng/table';
import { PlatformCompanyLogoComponent } from '../../../../components/company-logo/platform-company-logo.component';
import { PlatformApplicationStatus } from '../../../../enums/platform-application-status.enum';
import { PlatformApplicationSubTab } from '../../../../enums/platform-application-sub-tab.enum';
import { PlatformApplication } from '../../../../models/platform.models';
import { PlatformJobsService } from '../../../../services/platform-jobs.service';
import { PlatformFollowUpChoiceModalComponent } from '../../../../components/follow-up-choice-modal/platform-follow-up-choice-modal.component';
import { PlatformJobsCompleteModalComponent } from '../jobs-complete-modal/platform-jobs-complete-modal.component';
import { PlatformJobsEmptyStateComponent } from '../jobs-empty-state/platform-jobs-empty-state.component';

@Component({
  selector: 'app-platform-jobs-applications',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    ButtonComponent,
    ConfirmModalComponent,
    EmptyPlaceholderComponent,
    HelpTooltipComponent,
    SegmentedControlComponent,
    TableSearchInputComponent,
    TagComponent,
    FontAwesomeModule,
    TooltipDirective,
    TranslocoPipe,
    TableModule,
    PlatformCompanyLogoComponent,
    PlatformFollowUpChoiceModalComponent,
    PlatformJobsCompleteModalComponent,
    PlatformJobsEmptyStateComponent,
  ],
  templateUrl: './platform-jobs-applications.component.html',
  styleUrl: './platform-jobs-applications.component.scss',
})
export class PlatformJobsApplicationsComponent {
  readonly jobs = inject(PlatformJobsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastsService = inject(ToastsService);
  private readonly transloco = inject(TranslocoService);

  readonly TagVariant = TagVariant;
  readonly icons = {
    briefcase: faBriefcase,
    search: faSearch,
    trash: faTrash,
    sort: faSort,
    sortUp: faSortUp,
    sortDown: faSortDown,
  };

  readonly completingApplication = signal<PlatformApplication | null>(null);
  readonly deletingApplication = signal<PlatformApplication | null>(null);
  readonly statusFilter = signal(this.resolveInitialStatus());
  readonly searchQuery = signal<string | null>(null);

  readonly statusItems = computed(
    (): ReadonlyArray<SegmentedControlItem<PlatformApplicationSubTab>> => [
      {
        label: 'jobboard.platform.jobs.applications.tabs.all',
        value: PlatformApplicationSubTab.ALL,
        tag: {
          label: String(this.jobs.applications().length),
          type: TagVariant.COUNTER_DEFAULT,
        },
      },
      {
        label: 'jobboard.platform.jobs.applications.tabs.incomplete',
        value: PlatformApplicationSubTab.INCOMPLETE,
        tag: {
          label: String(this.jobs.incompleteApplications().length),
          type: TagVariant.COUNTER_DEFAULT,
        },
      },
      {
        label: 'jobboard.platform.jobs.applications.tabs.in-progress',
        value: PlatformApplicationSubTab.IN_PROGRESS,
        tag: {
          label: String(this.jobs.inProgressApplications().length),
          type: TagVariant.COUNTER_DEFAULT,
        },
      },
      {
        label: 'jobboard.platform.jobs.applications.tabs.process-ended',
        value: PlatformApplicationSubTab.PROCESS_ENDED,
        tag: {
          label: String(this.jobs.processEndedApplications().length),
          type: TagVariant.COUNTER_DEFAULT,
        },
      },
    ]
  );

  readonly visibleApplications = computed(() => {
    let applications: PlatformApplication[];
    switch (this.statusFilter()) {
      case PlatformApplicationSubTab.INCOMPLETE:
        applications = this.jobs.incompleteApplications();
        break;
      case PlatformApplicationSubTab.IN_PROGRESS:
        applications = this.jobs.inProgressApplications();
        break;
      case PlatformApplicationSubTab.PROCESS_ENDED:
        applications = this.jobs.processEndedApplications();
        break;
      default:
        applications = this.jobs.applications();
    }
    const search = this.searchQuery()?.trim().toLowerCase() ?? '';
    if (!search) {
      return applications;
    }
    return applications.filter(
      (application) =>
        application.jobTitle.toLowerCase().includes(search) ||
        application.companyName.toLowerCase().includes(search) ||
        application.location.toLowerCase().includes(search)
    );
  });

  private readonly completeModal =
    viewChild.required<PlatformJobsCompleteModalComponent>('completeModal');
  private readonly choiceModal =
    viewChild.required<PlatformFollowUpChoiceModalComponent>('choiceModal');
  private readonly deleteModal =
    viewChild.required<ConfirmModalComponent>('deleteModal');

  /**
   * Updates the status filter and syncs the URL query.
   * @param status - Selected status segment
   */
  setStatusFilter(status: PlatformApplicationSubTab): void {
    this.statusFilter.set(status);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { status },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Updates the free-text search for the applications table.
   * @param search - Search query or null
   */
  onSearch(search: string | null): void {
    this.searchQuery.set(search);
  }

  /**
   * Tracks table rows by application id.
   * @param _index - Row index
   * @param application - Application row
   * @returns Stable row id
   */
  trackById(_index: number, application: PlatformApplication): number {
    return application.id;
  }

  /**
   * Public company jobboard URL for an application.
   * @param application - Application row
   * @returns Company jobboard path or null
   */
  companyBoardUrl(application: PlatformApplication): string | null {
    if (application.companyId == null) {
      return null;
    }
    return `/companies/${application.companyId}`;
  }

  /**
   * Resolves the status tag variant for an application.
   * @param application - Application row
   * @returns Tag variant
   */
  statusTagVariant(application: PlatformApplication): TagVariant {
    switch (application.status) {
      case PlatformApplicationStatus.INCOMPLETE:
        return TagVariant.YELLOW;
      case PlatformApplicationStatus.PROCESS_ENDED:
        return TagVariant.GRAY;
      default:
        return TagVariant.DEFAULT;
    }
  }

  /**
   * Builds the help-tooltip content for missing application data.
   * @param application - Application row
   * @returns HTML tooltip with stacked equal-width tags
   */
  missingDataTooltip(application: PlatformApplication): string {
    const label = this.transloco.translate(
      'jobboard.platform.jobs.applications.missing-label'
    );
    const items = application.missingData
      .map((type) =>
        this.transloco.translate(this.jobs.missingDataTranslationKey(type))
      )
      .map(
        (item) =>
          `<span class="missing-data-tooltip-tag">${item}</span>`
      )
      .join('');
    return `<div class="missing-data-tooltip"><span class="missing-data-tooltip-label">${label}</span><div class="missing-data-tooltip-list">${items}</div></div>`;
  }

  /**
   * Opens the follow-up choice modal for an incomplete application.
   * @param application - Application to complete
   */
  openFollowUpChoice(application: PlatformApplication): void {
    this.choiceModal().open(application);
  }

  /**
   * Opens the follow-up modal for an incomplete application.
   * @param applicationId - Tracked application id
   */
  openCompleteModal(applicationId: number): void {
    const application = this.jobs
      .applications()
      .find((item) => item.id === applicationId);
    if (!application) {
      return;
    }
    this.completingApplication.set(application);
    queueMicrotask(() => this.completeModal().open());
  }

  /**
   * Opens the tracking-removal confirm modal.
   * @param application - Application to remove
   */
  openDeleteModal(application: PlatformApplication): void {
    this.deletingApplication.set(application);
    this.deleteModal().modal.open();
  }

  /**
   * Removes the selected application from tracking.
   */
  confirmDelete(): void {
    const application = this.deletingApplication();
    if (!application) {
      return;
    }
    this.jobs.removeApplicationFromTracking(application.id);
    this.deletingApplication.set(null);
  }

  /**
   * Shows a success toast after follow-up data was saved.
   */
  onCompleted(): void {
    this.toastsService.success(
      'jobboard.platform.jobs.applications.complete-toast'
    );
    this.completingApplication.set(null);
  }

  /**
   * Resolves the initial status filter from query or incomplete count.
   * @returns Status segment to activate
   */
  private resolveInitialStatus(): PlatformApplicationSubTab {
    const requested = this.route.snapshot.queryParamMap.get('status');
    if (
      requested === PlatformApplicationSubTab.ALL ||
      requested === PlatformApplicationSubTab.INCOMPLETE ||
      requested === PlatformApplicationSubTab.IN_PROGRESS ||
      requested === PlatformApplicationSubTab.PROCESS_ENDED
    ) {
      return requested;
    }
    return this.jobs.incompleteApplications().length > 0
      ? PlatformApplicationSubTab.INCOMPLETE
      : PlatformApplicationSubTab.ALL;
  }
}
